// src/data/claimCheck.js
//
// RULING 277 (Bob, 2026-10-03: "agreed - go", on the assistant's point 6: "A one-page claim check built automatically
// for every claim before service would do most of what this deck did"). The Coyle review deck (YCP-GO342-2026-0013)
// did by hand what the claim page already knew: what is right, what is missing, what to confirm, and the date. This
// puts it in one box at the top of every claim page, in that order.
//
// It is a CHECK, never a decision. It states only what the record and the platform's own builders already hold:
//   - missing / check come from penaltyClaim.js claimFormModel() - the same lists the form prints (ruling 271);
//   - the deadline is the claim's own filing window (expiresAt, written at filing from claimRequirements.js);
//   - the presentment officer is the one the agreement profile names (ruling 262), quoted, never inferred.
// Nothing here writes, sends or decides. Pure.
const s = (v) => (v == null ? '' : String(v)).trim();
const isDate = (v) => /^\d{4}-\d{2}-\d{2}$/.test(s(v));

function dayCount(from, to) {
  const a = Date.parse(s(from) + 'T00:00:00Z');
  const b = Date.parse(s(to) + 'T00:00:00Z');
  return Number.isFinite(a) && Number.isFinite(b) ? Math.round((b - a) / 86400000) : null;
}

function ruleKey(r) {
  return s(r && (r.rule || r.ruleId)).replace(/^RULE\/ARTICLE\s*[—–-]\s*/i, '').toUpperCase().replace(/\s+/g, ' ');
}

// expiresAt may be a date string or a Firestore Timestamp-like value.
function windowDate(v) {
  if (!v) return '';
  if (typeof v === 'string') return s(v).slice(0, 10);
  if (typeof v.toDate === 'function') return v.toDate().toISOString().slice(0, 10);
  if (Number.isFinite(v.seconds)) return new Date(v.seconds * 1000).toISOString().slice(0, 10);
  return '';
}

// Forensic audit 2026-10-16: "today" was new Date().toISOString().slice(0, 10) - the UTC date. From 8pm Eastern (5pm
// Pacific) that is already tomorrow, so the filing window read one day shorter and showed CLOSED a day early on its
// last evening. Today is the calendar date where the reader is.
export function localToday(now) {
  const d = now instanceof Date ? now : new Date();
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

// claimCheck(claim, { model, documentsCount, today, requirements })
//   model        - claimFormModel() output ({ missing, check, sendable }); null while the page is loading it
//   documents    - how many documents are attached to the claim
//   today        - 'YYYY-MM-DD'
//   requirements - getClaimRequirements(claim.applicableAgreement) or null
export function claimCheck(claim, opts) {
  const c = claim || {};
  const o = opts || {};
  const right = []; const fix = []; const confirm = [];

  // WHAT IS RIGHT - only what is on the record.
  if (s(c.applicableAgreement)) right.push('Agreement: ' + s(c.applicableAgreement));
  const committee = s(o.committee || c.committee || c.generalCommittee);
  if (committee) right.push('Committee: ' + committee);
  if (s(c.scopeKey) && !/UNROUTED/i.test(s(c.scopeKey))) right.push('Routed to ' + s(c.scopeKey));
  const live = (Array.isArray(c.matchedRules) ? c.matchedRules : []).filter(r => r && !r.setAsideAt && ruleKey(r));
  const names = Array.from(new Set(live.map(ruleKey)));
  if (names.length) right.push('Rule(s) relied on: ' + names.join('; '));
  if (s(c.description)) right.push('The claimant’s account of what happened is on the claim');

  // WHAT TO FIX - the form's own list first, word for word.
  const model = o.model || null;
  (model && Array.isArray(model.missing) ? model.missing : []).forEach(m => fix.push('Missing: ' + m));
  if (live.length > names.length) fix.push('The rules list holds ' + live.length + ' entries for ' + names.length + ' rule(s). Set the repeats aside.');
  if (Number.isFinite(o.documentsCount) && o.documentsCount === 0) fix.push('No documents are attached. Attach the documents the claim relies on before it is served.');
  if (!s(c.presentedOn)) fix.push('Not yet served on the Carrier.');

  // WHAT TO CONFIRM - the form's own check list, each sentence once.
  Array.from(new Set(model && Array.isArray(model.check) ? model.check : [])).forEach(x => confirm.push(x));
  const officer = s(o.requirements && o.requirements.presentmentOfficer);
  if (officer && s(c.presentedTo)) confirm.push('The claim names ' + s(c.presentedTo) + '. The agreement profile names ' + officer + '. Confirm they agree.');

  // THE DATE - the claim's own filing window, never a guess.
  const due = windowDate(c.expiresAt);
  const today = isDate(o.today) ? o.today : localToday();   // forensic audit 2026-10-16: local, not UTC
  const deadline = isDate(due) ? { due, daysLeft: dayCount(today, due), served: !!s(c.presentedOn) } : null;

  const ready = !!(model && model.sendable) && fix.filter(f => !/^Not yet served/.test(f)).length === 0;
  return { ready, right, fix, confirm, deadline, loading: !model };
}
